import React from 'react'
import { ShoppingCart, Package, MapPin, ExternalLink } from 'lucide-react'

interface Shop {
  name: string
  url?: string
  price?: string
  currency?: string
  isLocal?: boolean
  available?: boolean
  logo?: string
}

interface MultiShopPreviewContentProps {
  data: {
    title?: string
    description?: string
    shops?: Shop[]
    bookCover?: string
    bookTitle?: string
    author?: string
    isbn?: string
    coverUrl?: string
    publishDate?: string
    primaryShop?: string
    fonts?: {
      heading?: string
      body?: string
    }
    textSizes?: {
      bookTitle?: string
      author?: string
      shopName?: string
      price?: string
      branding?: string
    }
    companyPosition?: 'top' | 'bottom'
  }
  branding: {
    companyName?: string
    primaryColor?: string
    textColor?: string
    secondaryColor?: string
    brandingType?: 'beyondpages' | 'custom' | 'none'
    wrapperColor?: string
    wrapperOpacity?: number
  }
  colorMode?: 'gradient' | 'structured'
  isPreview?: boolean
}

export const MultiShopPreviewContent: React.FC<MultiShopPreviewContentProps> = ({ 
  data, 
  branding,
  colorMode = 'gradient',
  isPreview = true
}) => {
  const shops = data.shops || []
  
  // In structured mode with white shop cards, we need dark text
  const shopCardTextColor = colorMode === 'structured' ? '#1F2937' : (branding.textColor || 'white')

  // Load Google Fonts if needed
  React.useEffect(() => {
    const fonts = new Set([data.fonts?.heading, data.fonts?.body].filter(Boolean))
    fonts.forEach(font => {
      if (font && font !== 'Inter') {
        const link = document.createElement('link')
        link.href = `https://fonts.googleapis.com/css2?family=${font.replace(' ', '+')}:wght@400;600;700&display=swap`
        link.rel = 'stylesheet'
        document.head.appendChild(link)
      }
    })
  }, [data.fonts])

  // Main content component (shared between gradient and structured modes)
  const MainContent = () => (
    <>
      {/* Company name at top */}
      {branding.brandingType !== 'none' && data.companyPosition === 'top' && (
        <div className="text-center pt-8 pb-4">
          {branding.brandingType === 'beyondpages' ? (
            <p className="text-sm opacity-80" style={{ 
              color: branding.textColor || 'white',
              fontFamily: data.fonts?.body || 'Inter' 
            }}>
              Powered by BeyondPages
            </p>
          ) : branding.brandingType === 'custom' && branding.companyName ? (
            <h2 className={`${data.textSizes?.branding || 'text-lg'} font-semibold`} style={{ 
              color: branding.textColor || 'white',
              fontFamily: data.fonts?.body || 'Inter' 
            }}>
              {branding.companyName}
            </h2>
          ) : null}
        </div>
      )}

      {/* Upper section - Book Info */}
      <div className="px-6 pt-8 pb-2">
        <div className="max-w-sm mx-auto">
          {/* Book Cover and Details */}
          <div className="mb-4">
            <div className="flex gap-4 items-start">
              {/* Book Cover */}
              {(data.coverUrl || data.bookCover) ? (
                <img 
                  src={data.coverUrl || data.bookCover} 
                  alt={data.bookTitle}
                  className="w-24 h-36 rounded-lg shadow-lg object-cover"
                />
              ) : (
                <div className="w-24 h-36 rounded-lg shadow-lg bg-gray-200 flex items-center justify-center">
                  <Package className="w-10 h-10 text-gray-400" />
                </div>
              )}
              
              {/* Book Details */}
              <div className="flex-1 text-left">
                <h1 className={`${data.textSizes?.bookTitle || 'text-2xl'} font-bold mb-1`} style={{ 
                  color: branding.textColor || 'white',
                  fontFamily: data.fonts?.heading || 'Inter'
                }}>
                  {data.bookTitle || 'Buchtitel'}
                </h1>
                <p className={`${data.textSizes?.author || 'text-base'} opacity-90 mb-2`} style={{ 
                  color: branding.textColor || 'white',
                  fontFamily: data.fonts?.body || 'Inter'
                }}>
                  von {data.author || 'Autor'}
                </p>
                {data.isbn && (
                  <p className="text-xs opacity-70" style={{ 
                    color: branding.textColor || 'white',
                    fontFamily: data.fonts?.body || 'Inter'
                  }}>
                    ISBN: {data.isbn}
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {/* Choose retailer message */}
          <div className="text-center mt-4 mb-4">
            <p className="text-sm font-medium" style={{
              color: branding.textColor || 'white',
              fontFamily: data.fonts?.body || 'Inter',
              opacity: 0.9
            }}>
              Wählen Sie Ihren Händler
            </p>
          </div>
        </div>
      </div>

      {/* Lower section - Shop List */}
      <div className={`px-6 pb-8 ${colorMode === 'structured' ? 'pt-6' : 'pt-2'}`}>
        <div className="max-w-sm mx-auto">
          {/* Shop Cards */}
          <div className="space-y-3">
            {shops.map((shop, index) => (
              <a
                key={index}
                href={shop.url || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="block backdrop-blur-sm rounded-lg p-3 transition-all hover:scale-105 no-underline"
                style={{
                  backgroundColor: colorMode === 'structured' 
                    ? 'white' 
                    : branding.wrapperColor ? 
                      `rgba(${parseInt(branding.wrapperColor.slice(1,3), 16)}, ${parseInt(branding.wrapperColor.slice(3,5), 16)}, ${parseInt(branding.wrapperColor.slice(5,7), 16)}, ${branding.wrapperOpacity || 0.5})` : 
                      'rgba(255, 255, 255, 0.15)',
                  boxShadow: colorMode === 'structured' ? '0 1px 3px rgba(0,0,0,0.1)' : '0 2px 10px rgba(0,0,0,0.1)',
                  border: colorMode === 'structured' ? '1px solid #E5E7EB' : 'none'
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {/* Shop Logo or Icon */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden ${colorMode === 'structured' ? 'bg-gray-50' : 'bg-white/20'}`}>
                      {shop.logo ? (
                        <img src={shop.logo} alt={shop.name} className="w-6 h-6 object-contain" />
                      ) : shop.isLocal ? (
                        <MapPin className="w-5 h-5" style={{ color: '#10B981' }} />
                      ) : (
                        <ShoppingCart className="w-5 h-5" style={{ color: branding.primaryColor || '#3B82F6' }} />
                      )}
                      {/* Add favicon as fallback */}
                      {!shop.logo && shop.favicon && (
                        <img 
                          src={shop.favicon} 
                          alt={shop.name} 
                          className="w-6 h-6 object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                      )}
                    </div>
                    
                    {/* Shop Info */}
                    <div>
                      <h3 className={`${data.textSizes?.shopName || 'text-base'} font-semibold`} style={{ 
                        color: branding.primaryColor || '#3B82F6',
                        fontFamily: data.fonts?.heading || 'Inter'
                      }}>
                        {shop.name}
                      </h3>
                      {shop.isLocal && (
                        <p className="text-xs" style={{ 
                          color: '#10B981',
                          fontFamily: data.fonts?.body || 'Inter'
                        }}>
                          Lokaler Händler
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Price and Arrow */}
                  <div className="flex items-center gap-3">
                    {shop.price && (
                      <span className={`${data.textSizes?.price || 'text-sm'} font-bold`} style={{ 
                        color: shopCardTextColor,
                        fontFamily: data.fonts?.body || 'Inter'
                      }}>
                        {shop.price} {shop.currency || '€'}
                      </span>
                    )}
                    <ExternalLink className="w-4 h-4" style={{ 
                      color: branding.primaryColor || '#3B82F6',
                      opacity: 0.6
                    }} />
                  </div>
                </div>
              </a>
            ))}
          </div>

          {/* Add Shop Button (for preview mode) */}
          {shops.length === 0 && (
            <div className="text-center py-8">
              <div 
                className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-3"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
              >
                <ShoppingCart className="w-8 h-8" style={{ color: branding.textColor || 'white' }} />
              </div>
              <p className={`${data.textSizes?.shopName || 'text-base'} opacity-70`} style={{ 
                color: branding.textColor || 'white',
                fontFamily: data.fonts?.body || 'Inter'
              }}>
                Fügen Sie Shops hinzu
              </p>
            </div>
          )}

          {/* Company name at bottom */}
          {branding.brandingType !== 'none' && data.companyPosition === 'bottom' && (
            <div className="text-center mt-6">
              {branding.brandingType === 'beyondpages' ? (
                <p className="text-sm opacity-80" style={{ 
                  color: branding.textColor || 'white',
                  fontFamily: data.fonts?.body || 'Inter' 
                }}>
                  Powered by BeyondPages
                </p>
              ) : branding.brandingType === 'custom' && branding.companyName ? (
                <h2 className={`${data.textSizes?.branding || 'text-lg'} font-semibold`} style={{ 
                  color: branding.textColor || 'white',
                  fontFamily: data.fonts?.body || 'Inter' 
                }}>
                  {branding.companyName}
                </h2>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </>
  )

  return (
    <div className={`h-full flex flex-col bg-gray-50 ${isPreview ? 'pt-14 sm:pt-4' : 'pt-0'}`}>
      {/* Content */}
      <div className="flex-1 overflow-y-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {colorMode === 'gradient' ? (
          // Gradient mode - single continuous gradient
          <div className="min-h-full relative" style={{
            background: `linear-gradient(to bottom, ${branding.primaryColor} 0%, ${branding.secondaryColor} 100%)`
          }}>
            <MainContent />
          </div>
        ) : (
          // Structured mode - 65/35 split
          <div className="min-h-full relative">
            {/* Fixed background split */}
            <div className="absolute inset-0 flex flex-col">
              <div style={{ backgroundColor: branding.primaryColor, height: '45%' }} />
              <div className="flex-1" style={{ backgroundColor: branding.secondaryColor }} />
            </div>
            
            {/* Content layer */}
            <div className="relative z-10">
              <MainContent />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}